import type { ClassValue, HTMLButtonAttributes } from "svelte/elements";
import type { Icon, IconProps } from "../../../vite/Icon";
import { tw } from "../../utils/css/tw";

export enum ButtonVariant {
	Primary = "Primary",
	Secondary = "Secondary",
	Success = "Success",
	Error = "Error",
	Neutral = "Neutral",
}

export enum ButtonSize {
	Xs = "Xs",
	Sm = "Sm",
	Md = "Md",
	Lg = "Lg",
}

export enum ButtonVisualStyle {
	Filled = "Filled",
	Outlined = "Outlined",
	Text = "Text",
}

export interface ButtonProps extends HTMLButtonAttributes {
	/** @default {@link ButtonSize.Lg} */
	size?: ButtonSize;
	variant: ButtonVariant;
	/** @default {@link ButtonVisualStyle.Filled} */
	visualStyle?: ButtonVisualStyle;
	startIcon?: Icon;
	endIcon?: Icon;
	loading?: boolean;
	loadingIconAttrs?: IconProps;
	startIconAttrs?: IconProps;
	endIconAttrs?: IconProps;
	/** @default false */
	circle?: boolean;
}

export interface ButtonStyleProps {
	variant: ButtonVariant;
	size: ButtonSize;
	visualStyle: ButtonVisualStyle;
	circle?: boolean;
}

const variantStyles: Record<ButtonVariant, Record<ButtonVisualStyle, ClassValue>> = {
	[ButtonVariant.Primary]: {
		[ButtonVisualStyle.Filled]: tw(
			"bg-primary-200 text-primary-800 not-aria-disabled:hover:bg-primary-300 aria-disabled:bg-secondary-100 aria-disabled:text-secondary-400 border border-transparent",
		),
		[ButtonVisualStyle.Outlined]: tw(
			"border-primary-300 text-primary-600 not-aria-disabled:hover:bg-primary-50 aria-disabled:border-secondary-200 aria-disabled:text-secondary-400 border",
		),
		[ButtonVisualStyle.Text]: tw(
			"text-primary-600 not-aria-disabled:hover:bg-primary-50 aria-disabled:text-secondary-400 border border-transparent",
		),
	},
	[ButtonVariant.Secondary]: {
		[ButtonVisualStyle.Filled]: tw(
			"bg-secondary-600 not-aria-disabled:hover:bg-secondary-500 aria-disabled:bg-secondary-100 aria-disabled:text-secondary-400 border border-transparent text-white",
		),
		[ButtonVisualStyle.Outlined]: tw(
			"border-secondary-300 text-secondary-600 not-aria-disabled:hover:bg-secondary-transparent-5 aria-disabled:border-secondary-200 aria-disabled:text-secondary-400 border",
		),
		[ButtonVisualStyle.Text]: tw(
			"text-secondary-600 not-aria-disabled:hover:bg-secondary-50 aria-disabled:text-secondary-400 border border-transparent",
		),
	},
	[ButtonVariant.Success]: {
		[ButtonVisualStyle.Filled]: tw(
			"aria-disabled:bg-secondary-100 aria-disabled:text-secondary-400 border border-transparent bg-green-600 text-white not-aria-disabled:hover:bg-green-700",
		),
		[ButtonVisualStyle.Outlined]: tw(
			"aria-disabled:border-secondary-200 aria-disabled:text-secondary-400 border border-green-300 text-green-600 not-aria-disabled:hover:bg-green-50",
		),
		[ButtonVisualStyle.Text]: tw(
			"aria-disabled:text-secondary-400 border border-transparent text-green-600 not-aria-disabled:hover:bg-green-50",
		),
	},
	[ButtonVariant.Error]: {
		[ButtonVisualStyle.Filled]: tw(
			"aria-disabled:bg-secondary-100 aria-disabled:text-secondary-400 border border-transparent bg-red-500 text-white not-aria-disabled:hover:bg-red-600",
		),
		[ButtonVisualStyle.Outlined]: tw(
			"aria-disabled:border-secondary-200 aria-disabled:text-secondary-400 border border-red-300 text-red-500 not-aria-disabled:hover:bg-red-50",
		),
		[ButtonVisualStyle.Text]: tw(
			"aria-disabled:text-secondary-400 border border-transparent text-red-500 not-aria-disabled:hover:bg-red-50",
		),
	},
	[ButtonVariant.Neutral]: {
		[ButtonVisualStyle.Filled]: tw(
			"aria-disabled:bg-secondary-100 aria-disabled:text-secondary-400 bg-secondary-100 text-secondary-700 not-aria-disabled:hover:bg-secondary-200 border border-transparent",
		),
		[ButtonVisualStyle.Outlined]: tw(
			"aria-disabled:border-secondary-200 aria-disabled:text-secondary-400 border-secondary-300 text-secondary-600 not-aria-disabled:hover:bg-secondary-50 border bg-white",
		),
		[ButtonVisualStyle.Text]: tw(
			"aria-disabled:text-secondary-400 text-secondary-600 not-aria-disabled:hover:bg-secondary-100 border border-transparent",
		),
	},
};

const sizeStyles: Record<ButtonSize, ClassValue> = {
	[ButtonSize.Xs]: tw("rounded-md px-2 py-1 text-xs"),
	[ButtonSize.Sm]: tw("rounded-md px-3 py-1.25 text-xs"),
	[ButtonSize.Md]: tw("rounded-lg px-4 py-2 text-sm"),
	[ButtonSize.Lg]: tw("rounded-lg px-4 py-2 text-sm"),
};

const circleSizeStyles: Record<ButtonSize, ClassValue> = {
	[ButtonSize.Xs]: tw("rounded-full p-1 text-sm"),
	[ButtonSize.Sm]: tw("rounded-full p-1.25 text-xs"),
	[ButtonSize.Md]: tw("rounded-full p-2 text-sm"),
	[ButtonSize.Lg]: tw("rounded-full p-2 text-base"),
};

export function getButtonStyles({ variant, size, visualStyle, circle }: ButtonStyleProps): ClassValue {
	return tw(
		"focus-ring inline-flex cursor-pointer items-center gap-1.5 font-semibold not-motion-reduce:transition-all aria-disabled:cursor-not-allowed",
		circle ? circleSizeStyles[size] : sizeStyles[size],
		variantStyles[variant][visualStyle],
	);
}

const iconSizeStyles: Record<ButtonSize, ClassValue> = {
	[ButtonSize.Xs]: tw("size-4"),
	[ButtonSize.Sm]: tw("size-4.5"),
	[ButtonSize.Md]: tw("size-4.5"),
	[ButtonSize.Lg]: tw("size-6"),
};

export function getButtonIconStyles({ size }: ButtonStyleProps): ClassValue {
	return tw(iconSizeStyles[size]);
}
